// ======================================================================
// FILE:
// src/modules/finance/transactions/receipt_voucher/pages/ReceiptVoucherCreatePage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Receipt Voucher - Edit
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import receiptVoucherService
    from "../services/receiptVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// OPTIONS
// ======================================================================

const PAYER_TYPE_OPTIONS = [
    {
        value: "BUSINESS_PARTNER",
        label: "Business Partner",
    },
    {
        value: "VENDOR",
        label: "Vendor",
    },
    {
        value: "CUSTOMER",
        label: "Customer",
    },
    {
        value: "EMPLOYEE",
        label: "Employee",
    },
    {
        value: "GOVERNMENT",
        label: "Government / Tax Authority",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const RECEIPT_MODE_OPTIONS = [
    {
        value: "BANK_TRANSFER",
        label: "Bank Transfer",
    },
    {
        value: "NEFT",
        label: "NEFT",
    },
    {
        value: "RTGS",
        label: "RTGS",
    },
    {
        value: "IMPS",
        label: "IMPS",
    },
    {
        value: "CHEQUE",
        label: "Cheque",
    },
    {
        value: "CASH",
        label: "Cash",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const ALLOCATION_TYPE_OPTIONS = [
    {
        value: "MANUAL",
        label: "Manual",
    },
    {
        value: "INVOICE",
        label: "Invoice",
    },
    {
        value: "ADVANCE",
        label: "Advance",
    },
    {
        value: "TAX",
        label: "Tax",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


// ======================================================================
// INITIAL STATE
// ======================================================================

const getInitialForm = () => ({
    company_id: null,

    company_option: null,

    voucher_date:
        new Date()
            .toISOString()
            .slice(0, 10),

    receipt_account_id: null,

    receipt_account_option: null,

    payer_type: "",

    payer_id: null,

    payer_option: null,

    payer_name: "",

    receipt_mode: "",

    reference_no: "",

    reference_date: "",

    currency_id: null,

    currency_option: null,

    exchange_rate: "1",

    remarks: "",
});


const getInitialLine = (
    sequenceNo = 1
) => ({
    sequence_no: sequenceNo,

    account_id: null,

    account_option: null,

    allocation_type: "MANUAL",

    business_partner_id: null,

    vendor_id: null,

    customer_id: null,

    employee_id: null,

    transaction_amount: "",

    base_amount: "",

    cost_center_id: null,

    cost_center_option: null,

    project_id: null,

    project_option: null,

    department_id: null,

    description: "",

    source_type: "",

    source_id: null,

    source_reference: "",
});


// ======================================================================
// COMPONENT
// ======================================================================

export default function ReceiptVoucherEditPage() {

    const navigate =
        useNavigate();

    const {
        id
    } = useParams();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        getInitialForm()
    );


    const [
        lines,
        setLines
    ] = useState([
        getInitialLine(1),
    ]);


    const [
        saving,
        setSaving
    ] = useState(false);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // LOAD EXISTING RECEIPT VOUCHER
    // ==================================================================

    useEffect(
        () => {
            loadVoucher();
        },
        [
            id
        ]
    );


    function makeOption(
        value,
        label,
        data = null
    ) {

        if (!value) {
            return null;
        }

        return {
            value: value,
            label: label || `ID: ${value}`,
            data: data || null,
        };

    }


    async function loadVoucher() {

        try {

            setLoading(true);
            setErrorMessage("");

            const response =
                await receiptVoucherService
                    .getReceiptVoucher(
                        id
                    );

            if (!response) {
                throw new Error(
                    "Receipt Voucher not found."
                );
            }

            if (
                String(
                    response.status ||
                    ""
                ).toUpperCase() !== "DRAFT"
            ) {
                throw new Error(
                    "Only Draft Receipt Vouchers can be edited."
                );
            }

            const companyOption =
                makeOption(
                    response.company_id,
                    response.company_name ||
                    response.company?.company_name ||
                    response.company?.name
                );

            const receiptAccountOption =
                makeOption(
                    response.receipt_account_id,
                    response.receipt_account_name ||
                    response.receipt_account?.account_name ||
                    response.receipt_account?.name ||
                    response.receipt_account?.account_number
                );

            const currencyOption =
                makeOption(
                    response.currency_id,
                    response.currency_name ||
                    response.currency_code ||
                    response.currency?.currency_name ||
                    response.currency?.currency_code ||
                    response.currency?.name
                );

            let payerOption = null;

            if (response.payer_id) {
                payerOption =
                    makeOption(
                        response.payer_id,
                        response.payer_display_name ||
                        response.payer_name ||
                        response.payer?.bp_name ||
                        response.payer?.name
                    );
            }

            const loadedLines =
                Array.isArray(
                    response.lines
                )
                    ? response.lines
                        .filter(
                            line =>
                                !line.is_deleted
                        )
                        .sort(
                            (a, b) =>
                                Number(
                                    a.sequence_no || 0
                                ) -
                                Number(
                                    b.sequence_no || 0
                                )
                        )
                    : [];

            const mappedLines =
                loadedLines.length
                    ? loadedLines.map(
                        (line, index) => ({

                            sequence_no:
                                index + 1,

                            account_id:
                                line.account_id ||
                                null,

                            account_option:
                                makeOption(
                                    line.account_id,
                                    line.account_name ||
                                    line.account?.account_name ||
                                    line.account?.name ||
                                    line.account_code
                                        ? `${line.account_code || ""}${line.account_code && line.account_name ? " - " : ""}${line.account_name || line.account?.account_name || line.account?.name || ""}`.replace(/^ - /, "")
                                        : null
                                ),

                            allocation_type:
                                line.allocation_type ||
                                "MANUAL",

                            business_partner_id:
                                line.business_partner_id ||
                                null,

                            vendor_id:
                                line.vendor_id ||
                                null,

                            customer_id:
                                line.customer_id ||
                                null,

                            employee_id:
                                line.employee_id ||
                                null,

                            transaction_amount:
                                line.transaction_amount ??
                                "",

                            base_amount:
                                line.base_amount ??
                                "",

                            cost_center_id:
                                line.cost_center_id ||
                                null,

                            cost_center_option:
                                makeOption(
                                    line.cost_center_id,
                                    line.cost_center_name ||
                                    line.cost_center?.name ||
                                    line.cost_center?.cost_center_name
                                ),

                            project_id:
                                line.project_id ||
                                null,

                            project_option:
                                makeOption(
                                    line.project_id,
                                    line.project_name ||
                                    line.project?.name
                                ),

                            department_id:
                                line.department_id ||
                                null,

                            description:
                                line.description ||
                                "",

                            source_type:
                                line.source_type ||
                                "",

                            source_id:
                                line.source_id ||
                                null,

                            source_reference:
                                line.source_reference ||
                                "",

                        })
                    )
                    : [
                        getInitialLine(1)
                    ];

            setFormData({

                company_id:
                    response.company_id ||
                    null,

                company_option:
                    companyOption,

                voucher_date:
                    response.voucher_date
                        ? String(
                            response.voucher_date
                        ).slice(0, 10)
                        : "",

                receipt_account_id:
                    response.receipt_account_id ||
                    null,

                receipt_account_option:
                    receiptAccountOption,

                payer_type:
                    response.payer_type ||
                    "",

                payer_id:
                    response.payer_id ||
                    null,

                payer_option:
                    payerOption,

                payer_name:
                    response.payer_name ||
                    "",

                receipt_mode:
                    response.receipt_mode ||
                    "",

                reference_no:
                    response.reference_no ||
                    "",

                reference_date:
                    response.reference_date
                        ? String(
                            response.reference_date
                        ).slice(0, 10)
                        : "",

                currency_id:
                    response.currency_id ||
                    null,

                currency_option:
                    currencyOption,

                exchange_rate:
                    response.exchange_rate ??
                    "1",

                remarks:
                    response.remarks ||
                    "",

            });

            setLines(
                mappedLines
            );

        }
        catch (error) {

            console.error(
                "Failed to load Receipt Voucher:",
                error
            );

            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load Receipt Voucher."
            );

        }
        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // GENERIC FIELD CHANGE
    // ==================================================================

    function handleChange(
        field,
        value
    ) {

        setFormData(
            previous => ({
                ...previous,
                [field]: value,
            })
        );

    }


    // ==================================================================
    // COMPANY LOADER
    // ==================================================================

    async function loadCompanies(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                company => ({
                    value:
                        company.id,

                    label:
                        company.company_name,

                    data:
                        company,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // RECEIPT ACCOUNT LOADER
    // ==================================================================

    async function loadReceiptAccounts(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchBankAccounts(
                        inputValue
                    );

            return (
                response || []
            ).map(
                account => ({
                    value:
                        account.id,

                    label:
                        account.account_name ||
                        account.account_number ||
                        `ID: ${account.id}`,

                    data:
                        account,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // CURRENCY LOADER
    // ==================================================================

    async function loadCurrencies(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCurrencies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                currency => ({
                    value:
                        currency.id,

                    label:
                        currency.currency_code
                            ? `${currency.currency_code} - ${currency.currency_name || ""}`
                            : currency.currency_name ||
                              currency.name ||
                              `ID: ${currency.id}`,

                    data:
                        currency,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // PAYER LOADER
    // ==================================================================

    async function loadPayers(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchBusinessPartners(
                        inputValue
                    );

            return (
                response || []
            ).map(
                payer => ({
                    value:
                        payer.id,

                    label:
                        payer.name ||
                        payer.bp_name ||
                        payer.customer_name ||
                        payer.vendor_name ||
                        `ID: ${payer.id}`,

                    data:
                        payer,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // ACCOUNT LOADER
    // ==================================================================

    async function loadCOAAccounts(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchChartOfAccounts(
                        inputValue
                    );

            return (
                response || []
            ).map(
                account => ({
                    value:
                        account.id,

                    label:
                        account.account_code
                            ? `${account.account_code} - ${account.account_name}`
                            : account.account_name ||
                              `ID: ${account.id}`,

                    data:
                        account,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // COST CENTER LOADER
    // ==================================================================

    async function loadCostCenters(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCostCenters(
                        inputValue
                    );

            return (
                response || []
            ).map(
                item => ({
                    value:
                        item.id,

                    label:
                        item.name ||
                        item.cost_center_name ||
                        `ID: ${item.id}`,

                    data:
                        item,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // PROJECT LOADER
    // ==================================================================

    async function loadProjects(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchProjects(
                        inputValue
                    );

            return (
                response || []
            ).map(
                item => ({
                    value:
                        item.id,

                    label:
                        item.name ||
                        item.project_name ||
                        `ID: ${item.id}`,

                    data:
                        item,
                })
            );

        }
        catch {

            return [];

        }

    }


    // ==================================================================
    // LINE CHANGE
    // ==================================================================

    function handleLineChange(
        index,
        field,
        value
    ) {

        setLines(
            previous =>
                previous.map(
                    (line, lineIndex) =>
                        lineIndex === index
                            ? {
                                ...line,
                                [field]: value,
                            }
                            : line
                )
        );

    }


    // ==================================================================
    // ADD LINE
    // ==================================================================

    function addLine() {

        setLines(
            previous => [
                ...previous,
                getInitialLine(
                    previous.length + 1
                ),
            ]
        );

    }


    // ==================================================================
    // REMOVE LINE
    // ==================================================================

    function removeLine(
        index
    ) {

        setLines(
            previous =>
                previous
                    .filter(
                        (_, lineIndex) =>
                            lineIndex !== index
                    )
                    .map(
                        (line, lineIndex) => ({
                            ...line,
                            sequence_no:
                                lineIndex + 1,
                        })
                    )
        );

    }


    // ==================================================================
    // CALCULATIONS
    // ==================================================================

    const totalAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.transaction_amount
                            ) || 0
                        ),
                    0
                ),
            [
                lines
            ]
        );


    const totalBaseAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.base_amount
                            ) ||
                            (
                                (
                                    Number(
                                        line.transaction_amount
                                    ) || 0
                                ) *
                                (
                                    Number(
                                        formData.exchange_rate
                                    ) || 1
                                )
                            )
                        ),
                    0
                ),
            [
                lines,
                formData.exchange_rate
            ]
        );


    // ==================================================================
    // FORMAT AMOUNT
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );

    }


    // ==================================================================
    // VALIDATION
    // ==================================================================

    function validateForm() {

        if (
            !formData.company_id
        ) {
            return "Company is required.";
        }

        if (
            !formData.voucher_date
        ) {
            return "Voucher Date is required.";
        }

        if (
            !formData.receipt_account_id
        ) {
            return "Receipt Account is required.";
        }

        if (
            !formData.payer_type
        ) {
            return "Payer Type is required.";
        }

        if (
            !formData.receipt_mode
        ) {
            return "Receipt Mode is required.";
        }

        if (
            !formData.currency_id
        ) {
            return "Currency is required.";
        }

        if (
            !formData.exchange_rate ||
            Number(
                formData.exchange_rate
            ) <= 0
        ) {
            return "Exchange Rate must be greater than zero.";
        }

        if (
            !lines.length
        ) {
            return "At least one allocation line is required.";
        }

        for (
            let index = 0;
            index < lines.length;
            index += 1
        ) {

            const line =
                lines[index];

            if (
                !line.account_id
            ) {
                return `Allocation Account is required on line ${index + 1}.`;
            }

            if (
                !line.allocation_type
            ) {
                return `Allocation Type is required on line ${index + 1}.`;
            }

            if (
                !line.transaction_amount ||
                Number(
                    line.transaction_amount
                ) <= 0
            ) {
                return `Transaction Amount must be greater than zero on line ${index + 1}.`;
            }

        }

        if (
            totalAmount <= 0
        ) {
            return "Total Receipt Amount must be greater than zero.";
        }

        return null;

    }


    // ==================================================================
    // BUILD PAYLOAD
    // ==================================================================

    function buildPayload() {

        return {

            company_id:
                formData.company_id,

            voucher_date:
                formData.voucher_date,

            receipt_account_id:
                formData.receipt_account_id,

            payer_type:
                formData.payer_type,

            payer_id:
                formData.payer_id,

            payer_name:
                formData.payer_name,

            receipt_mode:
                formData.receipt_mode,

            reference_no:
                formData.reference_no ||
                null,

            reference_date:
                formData.reference_date ||
                null,

            currency_id:
                formData.currency_id,

            exchange_rate:
                Number(
                    formData.exchange_rate
                ) || 1,

            remarks:
                formData.remarks ||
                null,

            lines:
                lines.map(
                    line => ({

                        sequence_no:
                            line.sequence_no,

                        account_id:
                            line.account_id,

                        allocation_type:
                            line.allocation_type,

                        business_partner_id:
                            line.business_partner_id ||
                            null,

                        customer_id:
                            line.customer_id ||
                            null,

                        vendor_id:
                            line.vendor_id ||
                            null,

                        employee_id:
                            line.employee_id ||
                            null,

                        transaction_amount:
                            Number(
                                line.transaction_amount
                            ) || 0,

                        base_amount:
                            Number(
                                line.base_amount
                            ) ||
                            (
                                (
                                    Number(
                                        line.transaction_amount
                                    ) || 0
                                ) *
                                (
                                    Number(
                                        formData.exchange_rate
                                    ) || 1
                                )
                            ),

                        cost_center_id:
                            line.cost_center_id ||
                            null,

                        project_id:
                            line.project_id ||
                            null,

                        department_id:
                            line.department_id ||
                            null,

                        description:
                            line.description ||
                            null,

                        source_type:
                            line.source_type ||
                            null,

                        source_id:
                            line.source_id ||
                            null,

                        source_reference:
                            line.source_reference ||
                            null,

                    })
                ),

        };

    }


    // ==================================================================
    // SAVE
    // ==================================================================

    async function handleSaveDraft() {

        const validationError =
            validateForm();

        if (
            validationError
        ) {

            setErrorMessage(
                validationError
            );

            return;

        }

        try {

            setSaving(true);
            setErrorMessage("");

            await receiptVoucherService
                .updateReceiptVoucher(
                    id,
                    buildPayload()
                );

            navigate(
                `/finance/receipt-vouchers/view/${id}`
            );

        }
        catch (error) {

            console.error(
                "Failed to update Receipt Voucher:",
                error
            );

            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to update Receipt Voucher."
            );

        }
        finally {

            setSaving(false);

        }

    }


    // ==================================================================
    // CANCEL
    // ==================================================================

    function handleCancel() {

        navigate(
            `/finance/receipt-vouchers/view/${id}`
        );

    }


    // ==================================================================
    // LOADING
    // ==================================================================

    if (loading) {

        return (

            <>

                <FinancePageHeader
                    title="Edit Receipt Voucher"
                    subtitle="Finance & Accounts"
                />

                <div
                    className="
                        d-flex
                        justify-content-center
                        align-items-center
                        py-5
                    "
                >

                    <div
                        className="
                            spinner-border
                        "
                        role="status"
                    />

                </div>

            </>

        );

    }


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            <FinancePageHeader
                title="Edit Receipt Voucher"
                subtitle="Finance & Accounts"
            />


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {
                            errorMessage
                        }

                    </div>

                )
            }


            {/* ==========================================================
                HEADER
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    mb-4
                "
            >

                <div
                    className="
                        card-header
                        fw-bold
                    "
                >

                    Receipt Voucher Details

                </div>


                <div
                    className="
                        card-body
                    "
                >

                    <div
                        className="
                            row
                            g-3
                        "
                    >

                        {/* ==================================================
                            COMPANY
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Company
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCompanies
                                }

                                value={
                                    formData.company_option
                                }

                                onChange={
                                    option => {

                                        setFormData(
                                            previous => ({
                                                ...previous,

                                                company_id:
                                                    option
                                                        ? option.value
                                                        : null,

                                                company_option:
                                                    option ||
                                                    null,
                                            })
                                        );

                                    }
                                }

                                isClearable

                                isDisabled

                            />

                        </div>


                        {/* ==================================================
                            VOUCHER DATE
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Voucher Date
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="date"

                                className="
                                    form-control
                                "

                                value={
                                    formData.voucher_date
                                }

                                onChange={
                                    event =>
                                        handleChange(
                                            "voucher_date",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            RECEIPT ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Receipt Account
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadReceiptAccounts
                                }

                                value={
                                    formData.receipt_account_option
                                }

                                onChange={
                                    option => {

                                        setFormData(
                                            previous => ({
                                                ...previous,

                                                receipt_account_id:
                                                    option
                                                        ? option.value
                                                        : null,

                                                receipt_account_option:
                                                    option ||
                                                    null,
                                            })
                                        );

                                    }
                                }

                                isClearable

                            />

                        </div>


                        {/* ==================================================
                            PAYER TYPE
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Payer Type
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <Select

                                options={
                                    PAYER_TYPE_OPTIONS
                                }

                                value={
                                    PAYER_TYPE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.payer_type
                                    ) ||
                                    null
                                }

                                onChange={
                                    option => {

                                        setFormData(
                                            previous => ({
                                                ...previous,

                                                payer_type:
                                                    option
                                                        ? option.value
                                                        : "",

                                                payer_id:
                                                    null,

                                                payer_option:
                                                    null,
                                            })
                                        );

                                    }
                                }

                                isClearable

                            />

                        </div>


                        {/* ==================================================
                            PAYER
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Payer

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadPayers
                                }

                                value={
                                    formData.payer_option
                                }

                                onChange={
                                    option => {

                                        setFormData(
                                            previous => ({
                                                ...previous,

                                                payer_id:
                                                    option
                                                        ? option.value
                                                        : null,

                                                payer_option:
                                                    option ||
                                                    null,

                                                payer_name:
                                                    option
                                                        ? option.label
                                                        : previous.payer_name,
                                            })
                                        );

                                    }
                                }

                                isClearable

                            />

                        </div>


                        {/* ==================================================
                            PAYER NAME
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Payer Name

                            </label>


                            <input

                                type="text"

                                className="
                                    form-control
                                "

                                value={
                                    formData.payer_name
                                }

                                onChange={
                                    event =>
                                        handleChange(
                                            "payer_name",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            RECEIPT MODE
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Receipt Mode
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <Select

                                options={
                                    RECEIPT_MODE_OPTIONS
                                }

                                value={
                                    RECEIPT_MODE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.receipt_mode
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleChange(
                                            "receipt_mode",
                                            option
                                                ? option.value
                                                : ""
                                        )
                                }

                                isClearable

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE NO
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Reference No.

                            </label>


                            <input

                                type="text"

                                className="
                                    form-control
                                "

                                value={
                                    formData.reference_no
                                }

                                onChange={
                                    event =>
                                        handleChange(
                                            "reference_no",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE DATE
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Reference Date

                            </label>


                            <input

                                type="date"

                                className="
                                    form-control
                                "

                                value={
                                    formData.reference_date
                                }

                                onChange={
                                    event =>
                                        handleChange(
                                            "reference_date",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            CURRENCY
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Currency
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCurrencies
                                }

                                value={
                                    formData.currency_option
                                }

                                onChange={
                                    option => {

                                        setFormData(
                                            previous => ({
                                                ...previous,

                                                currency_id:
                                                    option
                                                        ? option.value
                                                        : null,

                                                currency_option:
                                                    option ||
                                                    null,
                                            })
                                        );

                                    }
                                }

                                isClearable

                            />

                        </div>


                        {/* ==================================================
                            EXCHANGE RATE
                        ================================================== */}

                        <div
                            className="
                                col-md-4
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Exchange Rate
                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="number"

                                min="0"

                                step="0.000001"

                                className="
                                    form-control
                                "

                                value={
                                    formData.exchange_rate
                                }

                                onChange={
                                    event =>
                                        handleChange(
                                            "exchange_rate",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            REMARKS
                        ================================================== */}

                        <div
                            className="
                                col-md-12
                            "
                        >

                            <label
                                className="
                                    form-label
                                    fw-semibold
                                "
                            >

                                Remarks

                            </label>


                            <textarea

                                className="
                                    form-control
                                "

                                rows="3"

                                value={
                                    formData.remarks
                                }

                                onChange={
                                    event =>
                                        handleChange(
                                            "remarks",
                                            event.target.value
                                        )
                                }

                            />

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ALLOCATION LINES
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    mb-4
                "
            >

                <div
                    className="
                        card-header
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <span
                        className="
                            fw-bold
                        "
                    >

                        Receipt Allocation

                    </span>


                    <button

                        type="button"

                        className="
                            btn
                            btn-sm
                            btn-outline-primary
                        "

                        onClick={
                            addLine
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-1
                            "
                        />

                        Add Line

                    </button>

                </div>


                <div
                    className="
                        card-body
                        p-0
                    "
                >

                    <div
                        className="
                            table-responsive
                        "
                    >

                        <table
                            className="
                                table
                                table-bordered
                                table-hover
                                mb-0
                            "
                        >

                            <thead
                                className="
                                    table-light
                                "
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 50,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 260,
                                        }}
                                    >
                                        Account
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 160,
                                        }}
                                    >
                                        Allocation Type
                                    </th>

                                    <th>
                                        Cost Centre
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 150,
                                        }}
                                    >
                                        Amount
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 150,
                                        }}
                                    >
                                        Base Amount
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 220,
                                        }}
                                    >
                                        Description
                                    </th>

                                    <th
                                        style={{
                                            width: 70,
                                        }}
                                    />

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.map(
                                        (
                                            line,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    index
                                                }
                                            >

                                                <td
                                                    className="
                                                        text-center
                                                        align-middle
                                                    "
                                                >

                                                    {
                                                        index + 1
                                                    }

                                                </td>


                                                {/* ACCOUNT */}

                                                <td>

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadCOAAccounts
                                                        }

                                                        value={
                                                            line.account_option
                                                        }

                                                        onChange={
                                                            option => {

                                                                handleLineChange(
                                                                    index,
                                                                    "account_id",
                                                                    option
                                                                        ? option.value
                                                                        : null
                                                                );

                                                                handleLineChange(
                                                                    index,
                                                                    "account_option",
                                                                    option ||
                                                                    null
                                                                );

                                                            }
                                                        }

                                                        isClearable
                                                        menuPortalTarget={
                                                            document.body
                                                        }

                                                        menuPosition="fixed"

                                                        styles={{
                                                            menuPortal: base => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}

                                                    />

                                                </td>


                                                {/* ALLOCATION TYPE */}

                                                <td>

                                                    <Select

                                                        options={
                                                            ALLOCATION_TYPE_OPTIONS
                                                        }

                                                        value={
                                                            ALLOCATION_TYPE_OPTIONS.find(
                                                                option =>
                                                                    option.value ===
                                                                    line.allocation_type
                                                            ) ||
                                                            null
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "allocation_type",
                                                                    option
                                                                        ? option.value
                                                                        : "MANUAL"
                                                                )
                                                        }

                                                        isClearable={
                                                            false
                                                        }
                                                            menuPortalTarget={
                                                                document.body
                                                            }

                                                            menuPosition="fixed"

                                                            styles={{
                                                                menuPortal: base => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                }),
                                                            }}

                                                    />

                                                </td>
                                                <td>

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadCostCenters
                                                        }

                                                        value={
                                                            line.cost_center_option ||
                                                            null
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "cost_center_id",
                                                                    option
                                                                        ? option.value
                                                                        : null,
                                                                    option
                                                                )
                                                        }

                                                        isClearable

                                                        isDisabled={
                                                            !formData.company_id
                                                        }

                                                        placeholder="Select Cost Centre"

                                                        menuPortalTarget={
                                                            document.body
                                                        }

                                                        menuPosition="fixed"

                                                        styles={{
                                                            menuPortal: base => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}

                                                    />

                                                </td>            

                                                {/* AMOUNT */}

                                                <td>

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                            text-end
                                                        "

                                                        value={
                                                            line.transaction_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "transaction_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                    />

                                                </td>


                                                {/* BASE AMOUNT */}

                                                <td>

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                            text-end
                                                        "

                                                        value={
                                                            line.base_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "base_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        placeholder={
                                                            (
                                                                Number(
                                                                    line.transaction_amount
                                                                ) *
                                                                (
                                                                    Number(
                                                                        formData.exchange_rate
                                                                    ) || 1
                                                                )
                                                            ).toFixed(2)
                                                        }

                                                    />

                                                </td>


                                                {/* DESCRIPTION */}

                                                <td>

                                                    <input

                                                        type="text"

                                                        className="
                                                            form-control
                                                        "

                                                        value={
                                                            line.description
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "description",
                                                                    event.target.value
                                                                )
                                                        }

                                                        placeholder="Description"

                                                    />

                                                </td>


                                                {/* REMOVE */}

                                                <td
                                                    className="
                                                        text-center
                                                        align-middle
                                                    "
                                                >

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-sm
                                                            btn-outline-danger
                                                        "

                                                        onClick={() =>
                                                            removeLine(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            lines.length <= 1
                                                        }

                                                        title="Remove line"

                                                    >

                                                        <i
                                                            className="
                                                                fas
                                                                fa-trash
                                                            "
                                                        />

                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )

                                }

                            </tbody>


                            {/* ======================================================
                                TOTALS
                            ====================================================== */}

                            <tfoot>

                                <tr
                                    className="
                                        table-light
                                    "
                                >

                                    <td
                                        colSpan={3}
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        Total

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalBaseAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        colSpan={2}
                                    />

                                </tr>

                            </tfoot>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                FOOTER ACTIONS
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-end
                    gap-2
                    mt-4
                    mb-4
                "
            >

                <button

                    type="button"

                    className="
                        btn
                        btn-outline-secondary
                    "

                    onClick={
                        handleCancel
                    }

                    disabled={
                        saving
                    }

                >

                    Cancel

                </button>


                <button

                    type="button"

                    className="
                        btn
                        btn-primary
                    "

                    onClick={
                        handleSaveDraft
                    }

                    disabled={
                        saving
                    }

                >

                    {
                        saving ? (

                            <>

                                <span
                                    className="
                                        spinner-border
                                        spinner-border-sm
                                        me-2
                                    "
                                />

                                Saving...

                            </>

                        ) : (

                            <>

                                <i
                                    className="
                                        fas
                                        fa-save
                                        me-2
                                    "
                                />

                                Save Changes

                            </>

                        )
                    }

                </button>

            </div>

        </>

    );

}
